<template>
  <div v-if="show" :class="flavor === 'card' ? 'crmUserCardDrawer' : 'crmUserCard'">
    <div class="cardHeader">
      <h3 class="cardTitle">{{ providerName }} User</h3>
      <span v-if="saving" class="statusMuted">Saving…</span>
      <span v-else-if="pageSaved" class="statusSaved">Saved</span>
    </div>

    <p v-if="linkDisplay && !link.stale && !link.user_missing_from_roster" class="stateLine">
      Your {{ providerName }} User:
      <strong>{{ linkDisplay.full_name || linkDisplay.email }}</strong>
      <span class="dim" v-if="crmUserId" :title="crmUserId">({{ crmUserId }})</span>
    </p>
    <p v-else-if="link && link.stale" class="warn">
      This is from a previous {{ providerName }} connection — please re-select your {{ providerName }} User.
    </p>
    <p v-else-if="link && link.user_missing_from_roster" class="warn">
      Your selected {{ providerName }} User is no longer active — please re-select.
    </p>
    <p v-else class="hint">
      We couldn't match you automatically — search and select your {{ providerName }} User.
    </p>

    <SearchSelect
      :model-value="selectedCrmUserId"
      :options-function="fetchRoster"
      :allow-clear="true"
      clear-label="Unlink"
      :placeholder="`Select your ${providerName} User…`"
      search-placeholder="Search by name or email…"
      @update:model-value="onSelect"
    />

    <p class="hint">
      We use this to set the owner on the {{ crmRecordsLabel }} you create here.
    </p>

    <p v-if="error" class="warn">{{ error }}</p>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import SearchSelect from '../../components/SearchSelect.vue'
import { apiFetch } from '../../services/api'
import { useCrmExport } from '../composables/useCrmExport'

// 'section' = a ProfilePage section (top rule, page margins); 'card' = the
// Preferences drawer's SettingsCard look. Same DOM and logic either way.
withDefaults(defineProps<{ flavor?: 'section' | 'card' }>(), { flavor: 'section' })

const crmExport = useCrmExport()

const loading = ref(true)
const context = ref<any>(null)
const selectedCrmUserId = ref('')
const saving = ref(false)
const pageSaved = ref(false)
const error = ref('')
let savedTimer: ReturnType<typeof setTimeout> | null = null

const show = computed(() => !!context.value?.provider)
const providerName = computed(() => context.value?.provider_display_name || 'CRM')
const crmRecordsLabel = computed(() => {
  const p = context.value?.provider
  if (p === 'salesforce') return 'Accounts, Contacts, Leads and Activities'
  if (p === 'hubspot') return 'Companies, Contacts and Activities'
  return 'records and activities'
})
const link = computed<any>(() => context.value?.link || null)
const linkDisplay = computed<any>(() => link.value?.display || null)
const crmUserId = computed<string>(() => String(link.value?.crm_user_id || ''))

async function load() {
  try {
    context.value = await apiFetch('/crm/users/me')
    selectedCrmUserId.value = context.value?.link?.crm_user_id || ''
  } catch {
    context.value = null
  } finally {
    loading.value = false
  }
}

async function fetchRoster(q: string) {
  try {
    const r = await apiFetch(`/crm/users?q=${encodeURIComponent(q || '')}`)
    return r?.options || []
  } catch {
    return []
  }
}

function markSaved() {
  pageSaved.value = true
  if (savedTimer) clearTimeout(savedTimer)
  savedTimer = setTimeout(() => { pageSaved.value = false }, 2000)
}

async function onSelect(val: string) {
  selectedCrmUserId.value = val || ''
  saving.value = true
  error.value = ''
  try {
    context.value = await apiFetch('/crm/users/me', {
      method: 'PUT',
      body: { crm_user_id: val || null },
    })
    selectedCrmUserId.value = context.value?.link?.crm_user_id || ''
    markSaved()
    // The export attribution preview ("Owned by") derives from this link.
    void crmExport.refresh()
  } catch (e: any) {
    error.value = e?.message || 'Could not save your selection. Please try again.'
    selectedCrmUserId.value = context.value?.link?.crm_user_id || ''
  } finally {
    saving.value = false
  }
}

onMounted(load)
</script>

<style scoped>
.crmUserCard {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid var(--color-border, var(--border, #e5e7eb));
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 480px;
}
/* Drawer flavor: the SettingsCard shape (bg-surface, subtle border, radius). */
.crmUserCardDrawer {
  background: var(--color-bg-surface);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg);
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}
.crmUserCardDrawer .cardTitle { font-size: var(--fs-sm); }
.cardHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.cardTitle {
  font-family: var(--font-sans, sans-serif);
  font-size: var(--fs-md, 15px);
  font-weight: var(--fw-semibold, 600);
  color: var(--color-text-primary, var(--text));
  margin: 0;
}
.stateLine {
  font-size: 13px;
  color: var(--color-text-primary, var(--text));
  margin: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.stateLine .dim { color: var(--muted); }
.hint {
  color: var(--muted);
  font-size: 12px;
  line-height: 1.5;
  margin: 0;
}
.warn {
  color: var(--warning, #b45309);
  font-size: 12px;
  line-height: 1.5;
  margin: 0;
}
.statusMuted { font-size: 13px; color: var(--muted); }
.statusSaved { font-size: 13px; font-weight: 600; color: var(--success, #059669); }
</style>
